import INVENTORY_API_BASE_URL from "../../config/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Transf.css";
import alertify from "alertifyjs";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import logUserActivity from "../utils/logUserActivity";

const getBarcodes = (shelf) =>
  Array.isArray(shelf?.barcodeData) ? shelf.barcodeData : [];

const getShelfLabel = (shelf) => {
  const details = [
    shelf.Warehouse,
    shelf.rackno ? "Rack " + shelf.rackno : "",
    shelf.shelfno ? "Shelf " + shelf.shelfno : "",
    shelf.sku ? "SKU " + shelf.sku : "",
    getBarcodes(shelf).length.toLocaleString() + " barcodes",
  ].filter(Boolean);

  return details.join(" · ");
};

const getWarehouseForShelf = (shelfno) =>
  String(shelfno || "").startsWith("W2-") ? "Warehouse 2" : "Warehouse 1";

const getRackForShelf = (shelfno) =>
  String(shelfno || "").split("-").slice(0, -1).join("-");

const getAllShelfLocations = (shelves) => {
  const locations = new Map();
  const addLocation = (location) => {
    if (!location.shelfno) return;
    const current = locations.get(location.shelfno) || {};
    locations.set(location.shelfno, { ...current, ...location });
  };

  for (let rack = 1; rack <= 45; rack += 1) {
    const suffixes = rack >= 29 ? ["A", "B", "C"] : ["A", "B", "C", "D", "E", "F", "G"];
    suffixes.forEach((suffix) => {
      const rackno = String(rack);
      addLocation({
        Warehouse: "Warehouse 1",
        rackno,
        shelfno: rackno + "-" + suffix,
      });
    });
  }

  for (let rack = 1; rack <= 30; rack += 1) {
    const rackno = "W2-" + rack;
    ["A", "B", "C", "D"].forEach((suffix) => {
      addLocation({
        Warehouse: "Warehouse 2",
        rackno,
        shelfno: rackno + "-" + suffix,
      });
    });
  }

  shelves.forEach((shelf) => {
    if (shelf?.shelfno) {
      addLocation({
        Warehouse: shelf.Warehouse || getWarehouseForShelf(shelf.shelfno),
        rackno: shelf.rackno || getRackForShelf(shelf.shelfno),
        shelfno: shelf.shelfno,
      });
    }
  });

  return Array.from(locations.values()).sort((a, b) =>
    a.shelfno.localeCompare(b.shelfno, undefined, { numeric: true })
  );
};

const TransferBarcodes = () => {
  const [data, setData] = useState([]);
  const [selectedFromShelf, setSelectedFromShelf] = useState(null);
  const [selectedToShelf, setSelectedToShelf] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransferring, setIsTransferring] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadShelves = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const response = await axios.get(
        INVENTORY_API_BASE_URL + "/getManageshelf"
      );
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Unable to load shelf inventory:", error);
      setLoadError("Shelf inventory could not be loaded. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShelves();
  }, [loadShelves]);

  const sourceOptions = useMemo(
    () =>
      data
        .filter((shelf) => shelf?._id && shelf?.sku && shelf?.shelfno)
        .map((shelf) => ({
          value: String(shelf._id),
          label: getShelfLabel(shelf),
        })),
    [data]
  );

  const sourceShelf = data.find(
    (shelf) => String(shelf?._id) === selectedFromShelf?.value
  );
  const sourceBarcodes = getBarcodes(sourceShelf);
  const allShelfLocations = useMemo(() => getAllShelfLocations(data), [data]);
  const targetOptions = useMemo(() => {
    if (!sourceShelf) return [];

    return allShelfLocations
      .filter((location) => location.shelfno !== sourceShelf.shelfno)
      .map((location) => {
        const shelvesAtLocation = data.filter(
          (shelf) => shelf.shelfno === location.shelfno
        );
        const matchingSkuShelf = shelvesAtLocation.find(
          (shelf) => shelf.sku === sourceShelf.sku
        );
        const otherSkus = Array.from(
          new Set(
            shelvesAtLocation
              .map((shelf) => shelf.sku)
              .filter((sku) => sku && sku !== sourceShelf.sku)
          )
        );
        const locationLabel = [
          location.Warehouse,
          location.rackno ? "Rack " + location.rackno : "",
          "Shelf " + location.shelfno,
        ].filter(Boolean);
        const inventoryLabel = matchingSkuShelf
          ? "SKU " + sourceShelf.sku + " · " + getBarcodes(matchingSkuShelf).length.toLocaleString() + " barcodes"
          : otherSkus.length
            ? "Other SKU: " + otherSkus.join(", ") + " · add selected SKU on transfer"
            : "Empty for this SKU · create it on transfer";

        return {
          value: location.shelfno,
          label: locationLabel.concat(inventoryLabel).join(" · "),
          shelfno: location.shelfno,
          Warehouse: location.Warehouse,
          rackno: location.rackno,
          capacityTemplate: matchingSkuShelf || shelvesAtLocation[0] || null,
        };
      });
  }, [allShelfLocations, data, sourceShelf]);
  const targetShelf = data.find(
    (shelf) =>
      shelf.shelfno === selectedToShelf?.value && shelf.sku === sourceShelf?.sku
  );

  const handleTransfer = async () => {
    if (!sourceShelf) {
      alertify.warning("Select a source shelf.");
      return;
    }
    if (!selectedToShelf) {
      alertify.warning("Select a destination shelf location.");
      return;
    }
    if (sourceBarcodes.length === 0) {
      alertify.warning("The selected source shelf has no barcodes to transfer.");
      return;
    }

    const capacityTemplate = selectedToShelf.capacityTemplate;
    const templateCapacity = Number(capacityTemplate?.capacity);
    const capacity = Number.isFinite(templateCapacity)
      ? Math.max(templateCapacity, sourceBarcodes.length)
      : sourceBarcodes.length;
    const createDestinationPayload = targetShelf
      ? null
      : {
          shelfno: selectedToShelf.shelfno,
          Warehouse:
            selectedToShelf.Warehouse ||
            capacityTemplate?.Warehouse ||
            getWarehouseForShelf(selectedToShelf.shelfno),
          rackno:
            selectedToShelf.rackno ||
            capacityTemplate?.rackno ||
            getRackForShelf(selectedToShelf.shelfno),
          sku: sourceShelf.sku,
          title: sourceShelf.title,
          Medium: sourceShelf.Medium,
          edition: sourceShelf.edition,
          mrp: sourceShelf.mrp,
          capacity,
          presentquantity: 0,
          type: ["good", "bad"].includes(sourceShelf.type)
            ? sourceShelf.type
            : "good",
          emptyspace: capacity,
        };

    setIsTransferring(true);
    try {
      const toShelf = selectedToShelf.shelfno;
      const response = await axios.put(
        INVENTORY_API_BASE_URL + "/transfer-barcodes",
        {
          fromShelf: sourceShelf.shelfno,
          fromSku: sourceShelf.sku,
          toShelf,
          toSku: sourceShelf.sku,
          transferAll: true,
          ...(createDestinationPayload
            ? { createDestination: createDestinationPayload }
            : {}),
        }
      );

      const transferredCount =
        response.data?.transferredCount ?? sourceBarcodes.length;
      const capacityIncreasedBy = response.data?.capacityIncreasedBy ?? 0;

      try {
        await logUserActivity("TRANSFER_BARCODES", {
          fromShelf: sourceShelf.shelfno,
          fromSku: sourceShelf.sku,
          toShelf,
          toSku: sourceShelf.sku,
          transferredCount,
          destinationCreated: Boolean(response.data?.destinationCreated),
          capacityIncreasedBy,
        });
      } catch (activityError) {
        console.error("Unable to log shelf transfer activity:", activityError);
      }

      alertify.success(
        transferredCount.toLocaleString() +
          " barcodes transferred successfully" +
          (response.data?.destinationCreated ? " to the new shelf." : ".") +
          (capacityIncreasedBy
            ? " Destination capacity increased by " +
              Number(capacityIncreasedBy).toLocaleString() +
              "."
            : "")
      );
      setSelectedFromShelf(null);
      setSelectedToShelf(null);
      await loadShelves();
    } catch (error) {
      console.error("Unable to transfer shelf barcodes:", error);
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === "string"
          ? error.response.data
          : "Unable to transfer barcodes. Please refresh and try again.");
      alertify.error(message);
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <main className="transfer-barcodes-page">
      <div className="transfer-barcodes-page__inner">
        <header className="transfer-barcodes-hero">
          <p className="transfer-barcodes-eyebrow">
            <span aria-hidden="true" />
            WAREHOUSE OPERATIONS / INVENTORY MOVEMENT
          </p>
          <div className="transfer-barcodes-hero__content">
            <div>
              <h1>Transfer shelves</h1>
              <p>
                Move every barcode from one shelf to another without scanning.
              </p>
            </div>
            <div className="transfer-barcodes-total" aria-live="polite">
              <strong>{sourceBarcodes.length.toLocaleString()}</strong>
              <span>barcodes selected</span>
            </div>
          </div>
        </header>

        {loadError && (
          <div className="transfer-barcodes-error" role="alert">
            <span>{loadError}</span>
            <Button type="button" variant="outline-primary" onClick={loadShelves}>
              Retry
            </Button>
          </div>
        )}

        <div className="transfer-barcodes-layout">
          <div className="transfer-barcodes-workspace">
            <section
              className="transfer-barcodes-card"
              aria-labelledby="transfer-shelves-heading"
            >
              <div className="transfer-barcodes-card__heading">
                <span className="transfer-barcodes-step transfer-barcodes-step--green" aria-hidden="true">
                  01
                </span>
                <div>
                  <h2 id="transfer-shelves-heading">Choose the shelf route</h2>
                  <p>
                    Select the source first. The destination list includes every
                    shelf location in both warehouses.
                  </p>
                </div>
              </div>

              <div className="transfer-barcodes-shelf-route">
                <div className="transfer-barcodes-shelf-field">
                  <label
                    className="transfer-barcodes-field-label"
                    htmlFor="transfer-from-shelf"
                  >
                    From shelf
                  </label>
                  <Select
                    inputId="transfer-from-shelf"
                    classNamePrefix="transfer-select"
                    aria-label="Choose source shelf"
                    placeholder={isLoading ? "Loading shelves..." : "Select source shelf"}
                    onChange={(option) => {
                      setSelectedFromShelf(option);
                      setSelectedToShelf(null);
                    }}
                    options={sourceOptions}
                    value={selectedFromShelf}
                    isDisabled={isLoading || isTransferring}
                    isClearable
                  />
                  <p>Select the shelf that currently holds the barcodes.</p>
                </div>

                <span className="transfer-barcodes-route-arrow" aria-hidden="true">
                  →
                </span>

                <div className="transfer-barcodes-shelf-field">
                  <label
                    className="transfer-barcodes-field-label"
                    htmlFor="transfer-to-shelf"
                  >
                    To shelf
                  </label>
                  <Select
                    inputId="transfer-to-shelf"
                    classNamePrefix="transfer-select"
                    aria-label="Choose destination shelf"
                    placeholder={
                      sourceShelf
                        ? "Select any shelf location"
                        : "Choose the source shelf first"
                    }
                    onChange={setSelectedToShelf}
                    options={targetOptions}
                    value={selectedToShelf}
                    isDisabled={!sourceShelf || isLoading || isTransferring}
                    isClearable
                    noOptionsMessage={() => "No shelf locations found"}
                  />
                  <p>
                    All shelf locations are available. If this SKU is not already
                    there, its shelf record will be created during the transfer.
                  </p>
                  {selectedToShelf && !targetShelf && (
                    <p className="transfer-barcodes-new-location" role="status">
                      SKU {sourceShelf.sku} will be added to this shelf automatically.
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section
              className="transfer-barcodes-card transfer-barcodes-scanner"
              aria-labelledby="transfer-automatic-heading"
            >
              <div className="transfer-barcodes-card__heading">
                <span className="transfer-barcodes-step transfer-barcodes-step--dark" aria-hidden="true">
                  02
                </span>
                <div>
                  <h2 id="transfer-automatic-heading">Automatic barcode selection</h2>
                  <p>
                    Every barcode stored on the selected source shelf is included
                    automatically.
                  </p>
                </div>
              </div>
              <div className="transfer-barcodes-scanner-hint">
                <span aria-hidden="true" />
                {sourceShelf
                  ? sourceBarcodes.length
                    ? "No scan needed. " +
                      sourceBarcodes.length.toLocaleString() +
                      " barcodes are ready to move."
                    : "The selected source shelf currently has no barcodes."
                  : "Choose a source shelf to load all of its barcodes."}
              </div>
            </section>
          </div>

          <aside
            className="transfer-barcodes-card transfer-barcodes-queue"
            aria-labelledby="transfer-queue-heading"
          >
            <div className="transfer-barcodes-queue__heading">
              <div>
                <p className="transfer-barcodes-queue__eyebrow">TRANSFER SUMMARY</p>
                <h2 id="transfer-queue-heading">Ready to move</h2>
              </div>
              <span className="transfer-barcodes-queue__count">
                {sourceBarcodes.length.toLocaleString()}
              </span>
            </div>

            <div className="transfer-barcodes-route-summary">
              <div>
                <span>SKU</span>
                <strong>{sourceShelf?.sku || "No source selected"}</strong>
              </div>
              <div>
                <span>From</span>
                <strong>{selectedFromShelf?.label || "No source shelf"}</strong>
              </div>
              <div>
                <span>To</span>
                <strong>{selectedToShelf?.label || "No destination shelf"}</strong>
              </div>
            </div>

            <div className="transfer-barcodes-list-heading">
              <h3>Source shelf barcodes</h3>
              <span>{sourceBarcodes.length.toLocaleString()}</span>
            </div>

            {sourceBarcodes.length === 0 ? (
              <div className="transfer-barcodes-empty">
                <span className="transfer-barcodes-empty__icon" aria-hidden="true">
                  ↗
                </span>
                <strong>{sourceShelf ? "No barcodes on this shelf" : "Select a source shelf"}</strong>
                <p>
                  {sourceShelf
                    ? "Choose a shelf with barcodes to transfer."
                    : "All barcodes from the chosen source shelf will be included."}
                </p>
              </div>
            ) : (
              <>
                <ul className="transfer-barcodes-list">
                  {sourceBarcodes.slice(0, 8).map((barcode, index) => (
                    <li key={String(barcode) + "-" + index}>
                      <span className="transfer-barcodes-list__index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <code>{barcode}</code>
                      <span className="transfer-barcodes-list__check" aria-hidden="true">
                        ✓
                      </span>
                    </li>
                  ))}
                </ul>
                {sourceBarcodes.length > 8 && (
                  <p className="transfer-barcodes-queue__note">
                    Previewing 8 of {sourceBarcodes.length.toLocaleString()}; all
                    barcodes will transfer.
                  </p>
                )}
              </>
            )}

            <Button
              className="transfer-barcodes-submit"
              type="button"
              onClick={handleTransfer}
              disabled={
                !sourceShelf ||
                !selectedToShelf ||
                sourceBarcodes.length === 0 ||
                isLoading ||
                isTransferring
              }
            >
              {isTransferring
                ? "Transferring..."
                : "Transfer all " + sourceBarcodes.length.toLocaleString() + " barcodes"}
              <span aria-hidden="true">→</span>
            </Button>
            <p className="transfer-barcodes-queue__note">
              Confirm the source and destination shelves before moving the full
              barcode list.
            </p>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default TransferBarcodes;
